import { useState, useEffect, useMemo, useCallback } from 'react';
import { Box, Button, ClickableBox, EthSign, Flex, Message } from 'components/ui';
import { ArrowDown, ArrowUp } from 'components/ui/Arrows';
import { BigNumberish } from '@ethersproject/bignumber';
import { BigNumber } from 'ethers';
import { useTheme } from 'styled-components';
import { MIXPASS_SALE_CONFIG } from './Mixpass/mixpassConfig';
import { useWallet } from './WalletContext';
import useERC20TokenBalance from 'hooks/useERC20TokenBalance';
import styled from 'styled-components';
import { POLYGON_ETH_CONTRACT_ADDRESS } from '../constants';
import useTransactionHandler from 'hooks/useTransactionHandler';
import ApproveERC20 from './ApproveERC20';
import { ERC20Token } from './common';

const DexLink = styled.a`
  color: ${props => props.theme.colors.copy};
`;

export interface BuyWithERC20Props {
  maxCount: number;
  onClick(arg1: number): any;
  disabled?: boolean;
  buttonColor?: string;
  defaultCount?: number;
  tooltipId?: string;
  maxCountText?: string;
  submitButtonText?: string;
  price?: BigNumberish;
  networkId: number;
  onError?: any;
  showAllocationWarning: boolean;
  paymentToken: ERC20Token;
}

const BuyWithERC20: React.FC<BuyWithERC20Props> = ({
  maxCount,
  onClick,
  disabled = false,
  tooltipId,
  maxCountText,
  submitButtonText,
  price,
  paymentToken,
  networkId,
  onError,
  showAllocationWarning
}) => {
  const theme = useTheme();
  const { network, switchNetwork, isMetaMask } = useWallet();
  const { errorHandler } = useTransactionHandler();

  const [count, setCount] = useState<number>(0);

  function incrementCount() {
    if (count === maxCount) return;
    setCount(last => last + 1);
  }

  function decrementCount() {
    if (count <= 1) return;
    setCount(last => last - 1);
  }

  useEffect(() => {
    if (count > maxCount) {
      return setCount(maxCount);
    }

    if (count === 0 && maxCount > 0) {
      return setCount(maxCount);
    }
    // note: do not want this to trigger when count updates
    // eslint-disable-next-line
  }, [maxCount]);

  const totalCost = useMemo(() => BigNumber.from(price).mul(count).toString(), [price, count]);
  const userBalance = useERC20TokenBalance(paymentToken, networkId);
  const userHasEnoughFunds = userBalance && userBalance.gte(totalCost);
  const isConnectedToRightNetwork = networkId === network;

  const buyFromWhitelist = useCallback(async () => {
    try {
      await onClick(count);
    } catch (e) {
      if (onError) {
        onError(e);
      } else {
        errorHandler();
      }
    }
  }, [errorHandler, onClick, onError, count]);

  return (
    <Flex width="100%">
      <Flex wrap>
        <Flex width="100%" alignCenter>
          <Box
            border={`3px solid ${theme.colors.primary}`}
            py="2"
            px="3"
            fontSize="4"
            borderRadius="2"
          >
            {count}
          </Box>
          <Flex column alignCenter height="100%" ml="1" mr="3">
            <ArrowUp
              color="primary"
              size={10}
              mb="1"
              onClick={incrementCount}
              disabled={disabled}
            />
            <ArrowDown color="primary" size={10} onClick={decrementCount} disabled={disabled} />
          </Flex>

          {!isConnectedToRightNetwork ? (
            <Button disabled={!isMetaMask()} onClick={() => switchNetwork(networkId)}>
              Switch network
            </Button>
          ) : (
            <ApproveERC20
              amount={totalCost}
              paymentToken={paymentToken}
              spendingContractAddress={MIXPASS_SALE_CONFIG.contractAddress}
              approveLabel="Approve ETH"
              chainId={networkId}
            >
              <Button
                boxShadow="1"
                fontWeight="600"
                onClick={buyFromWhitelist}
                disabled={!userHasEnoughFunds || count === 0 || disabled}
                background={disabled ? 'primary' : theme.colors.secondary}
                data-tip="tooltip"
                data-for={tooltipId}
              >
                {submitButtonText ? `${submitButtonText} ` : ''}
              </Button>
            </ApproveERC20>
          )}
        </Flex>

        {showAllocationWarning && count > 0 && count < maxCount && (
          <Message mt={4} textAlign="left" background={theme.colors.warning}>
            <Box color="black" mb={1}>
              Warning: You are currently purchasing less than your full allocation. You will only get one shot at purchasing this allocation!
            </Box>
            <Box color="black" mb={1}>
              Keep in mind that Mixpass NFTs are burned when redeemed for Futura Mix NFTs. For those who choose NOT to redeem their Mixpass, there is a big bonus!
            </Box>
            <Box color="black">
              All holders of an unredeemed Mixpass NFT will receive a 25% discount on Remixing... ongoing, as long as the Mixpass is held. We'd like to reward those who choose to keep one.
            </Box>
          </Message>
        )}

        <Box>
          {!userHasEnoughFunds && (
            <Flex mt={2}>
              <Box mr={1} color="error">
                Not enough funds.
              </Box>
              <DexLink
                href={`https://quickswap.exchange/#/swap?outputCurrency=${POLYGON_ETH_CONTRACT_ADDRESS}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                Buy more ETH
              </DexLink>
            </Flex>
          )}

          <Flex alignCenter mt={3} justifyCenter mb="3">
            <Box mr={1}>{maxCountText}</Box>
            <ClickableBox
              borderBottom={`3px solid ${theme.colors.primary}`}
              onClick={() => setCount(maxCount)}
              color="primary"
            >
              {maxCount}
            </ClickableBox>
            <Flex alignCenter justifyCenter color="copy" ml={3} fontSize="2">
              <Box mr="2">Cost:</Box>
              <EthSign value={BigNumber.from(price).mul(count).toString()} />
            </Flex>
          </Flex>
        </Box>
      </Flex>
    </Flex>
  );
};

export default BuyWithERC20;
